import { ApolloLink, HttpLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { GrowthbookForwardHeaders, type StringMap } from 'corgi-types';
import { getGQLClientUri, sha256 } from 'frontend-utils';

import { Header } from '@local-types/enums/header';
import { getEnv } from '@utils/env';
import { logger } from '@utils/logger';
import { start } from '@utils/timer';
import type { GQLClientProps } from './client-props';

const ForwardHeaders = [
  ...Object.values(GrowthbookForwardHeaders),
  Header.TestMyAccountView,
  Header.TestCheckoutView,
  Header.TestCartView,
];

export const getForwardHeaders = (headers: StringMap<string>) =>
  ForwardHeaders.reduce((acc, header) => {
    if (headers[header]) {
      return { ...acc, [header]: headers[header] };
    }
    return acc;
  }, {});

export const getHttpLink = ({ featureFlags, headers }: GQLClientProps) =>
  new HttpLink({
    uri: getGQLClientUri(getEnv('GRAPHQL_ENDPOINT_URL'), featureFlags),
    credentials: 'include',
    headers,
  });

export const getLoggerLink = (requestId: string) =>
  new ApolloLink((operation, forward) => {
    const timer = start();

    return forward
      ? forward(operation).map(result => {
          const { cached } = operation.getContext();

          logger.debug(
            { requestId },
            cached
              ? `GQL "${operation.operationName}" response retrieved from cache.`
              : `GQL "${operation.operationName}" request duration: ${timer.end()}ms.`,
          );

          return result;
        })
      : null;
  });

export const persistedQueryLink = createPersistedQueryLink({
  useGETForHashedQueries: true,
  sha256,
});

// TEMPORARY: Enhanced error logging to identify GraphQL 422 errors
export const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const { operationName, variables } = operation;

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      logger.error(
        {
          operationName,
          variables: JSON.stringify(variables, null, 2),
          locations,
          path,
          extensions,
        },
        `[GraphQL Error] Operation: ${operationName}, Message: ${message}`,
      );
    });
  }

  if (networkError) {
    const errorDetails: Record<string, unknown> = {
      operationName,
      variables: JSON.stringify(variables, null, 2),
      statusCode: 'statusCode' in networkError ? networkError.statusCode : 'N/A',
      message: networkError.message,
    };

    // Try to extract response body details for 422 errors
    if ('result' in networkError && networkError.result) {
      errorDetails.responseErrors = JSON.stringify(networkError.result, null, 2);
    }

    logger.error(
      errorDetails,
      `[Network Error] Operation: ${operationName}, Status: ${errorDetails.statusCode}`,
    );
  }
});

const MIN_RETRY_DELAY = 250;
export const retryLink = new RetryLink({
  delay: (retryNumber: number) => {
    const delay = Math.pow(2, retryNumber - 1) * MIN_RETRY_DELAY;
    const randomMs = delay * 0.2 * Math.random(); // 0-20% of the delay

    return delay + randomMs;
  },
  attempts: {
    max: 5,
  },
});
